import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Grid, 
  Tab, 
  Tabs,
  Paper,
  Typography,
  useTheme,
  styled,
  Theme
} from '@mui/material';
import { FinancialMetricsChart } from '../Dashboard/components/FinancialMetricsChart';
import { RecentTransactions } from '../Dashboard/components/RecentTransactions';
import { useTransactionService } from '../../hooks/useTransactionService';
import { useReportingEngine } from '../../services/ReportingEngine';
import { useAuth } from '../../../contexts/AuthContext';
import { Transaction } from '../../types/accounting';
import { JournalEntries } from './JournalEntries';
import { ChartOfAccounts } from './ChartOfAccounts';
import { FundManagement } from './FundManagement';

// Define the extended TransactionFilters interface
interface ExtendedTransactionFilters {
  startDate?: Date;
  endDate?: Date;
  minAmount?: number;
  maxAmount?: number;
  status?: string;
  type?: string;
  fiscalSponsorId?: string; // Add this property
  limit?: number; // Add this property
  id?: string; // Add this property if needed
}

interface DisplayTransaction {
  id: string;
  date: string | Date;
  description: string;
  amount: number;
  type?: string;
  status?: string;
}

// Styled Components with dark theme colors and proper text contrast
const StyledTabs = styled(Tabs)(({ theme }: { theme: Theme }) => ({
  borderBottom: '1px solid rgba(255, 255, 255, 0.12)',
  '& .MuiTabs-indicator': {
    background: `linear-gradient(90deg, #0EA5E9 0%, #10B981 100%)`,
    height: 3
  }
}));

const StyledTab = styled(Tab)(({ theme }: { theme: Theme }) => ({
  color: 'rgba(255, 255, 255, 0.7)', // Unselected tabs in light gray
  '&.Mui-selected': {
    color: '#0EA5E9' // Selected tab in brand blue
  },
  '&:hover': {
    color: '#38BDF8',
    opacity: 1
  },
  fontWeight: 600,
  textTransform: 'uppercase'
}));

const GradientBorderPaper = styled(Paper)(({ theme }: { theme: Theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  backgroundColor: 'rgb(17, 25, 35)', // Dark charcoal background
  color: 'rgba(255, 255, 255, 0.9)', // Light text for content
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '4px',
    background: `linear-gradient(90deg, #0EA5E9 0%, #10B981 100%)`
  }
}));

export const AccountingDashboard: React.FC = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [metrics, setMetrics] = useState<any>(null);
  const [recentTransactions, setRecentTransactions] = useState<DisplayTransaction[]>([]);
  const theme = useTheme();
  
  const transactionService = useTransactionService();
  const reportingEngine = useReportingEngine();
  const { currentUser } = useAuth();

  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        const dashboardMetrics = await reportingEngine.generateDashboardMetrics({
          fiscalSponsorId: currentUser?.fiscalSponsorId
        });
        setMetrics(dashboardMetrics);

        if (currentUser?.fiscalSponsorId) {
          // Cast the filters to ExtendedTransactionFilters
          const transactions = await transactionService.getTransactions({
            fiscalSponsorId: currentUser.fiscalSponsorId,
            limit: 5
          } as ExtendedTransactionFilters);

          const formattedTransactions = transactions.map((transaction: Transaction) => ({
            id: transaction.id || '',
            date: new Date(transaction.date || Date.now()),
            description: transaction.description || 'No description',
            amount: Number(transaction.amount || 0),
            type: 'standard',
            status: transaction.status || 'pending'
          }));

          setRecentTransactions(formattedTransactions);
        }
      } catch (error) {
        console.error('Error loading dashboard data:', error);
      }
    };

    loadDashboardData();
  }, [currentUser, reportingEngine, transactionService]);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: string) => {
    setActiveTab(newValue);
  };

  return (
    <Box sx={{ 
      minHeight: '100vh',
      backgroundColor: 'rgb(17, 25, 35)', // Dark charcoal background
      p: 3,
      color: 'rgba(255, 255, 255, 0.9)' // Light text for content
    }}>
      <GradientBorderPaper 
        elevation={0} 
        sx={{ 
          mb: 3, 
          p: 2,
          backgroundColor: 'rgb(17, 25, 35)' // Dark charcoal background
        }}
      >
        <Typography 
          variant="h4" 
          gutterBottom 
          sx={{ 
            fontWeight: 600,
            color: 'rgba(255, 255, 255, 0.9)', // Light text for heading
            mb: 3
          }}
        >
          Accounting Dashboard
        </Typography>
        <StyledTabs 
          value={activeTab} 
          onChange={handleTabChange}
          sx={{
            backgroundColor: 'transparent'
          }}
        >
          <StyledTab value="overview" label="Overview" />
          <StyledTab value="journal-entries" label="Journal Entries" />
          <StyledTab value="chart-of-accounts" label="Chart of Accounts" />
          <StyledTab value="fund-management" label="Fund Management" />
        </StyledTabs>
      </GradientBorderPaper>

      {activeTab === 'overview' && (
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <GradientBorderPaper 
              sx={{ 
                p: 3,
                backgroundColor: 'rgb(17, 25, 35)' // Dark charcoal background
              }}
            >
              <FinancialMetricsChart 
                data={metrics?.trends?.revenue_trend || []}
              />
            </GradientBorderPaper>
          </Grid>
          
          <Grid item xs={12}>
            <GradientBorderPaper 
              sx={{ 
                p: 3,
                backgroundColor: 'rgb(17, 25, 35)' // Dark charcoal background
              }}
            >
              <RecentTransactions
                transactions={recentTransactions}
                onViewAll={() => {/* Add navigation logic */}}
              />
            </GradientBorderPaper>
          </Grid>
        </Grid>
      )}

      <Box sx={{ mt: 3 }}>
        {activeTab === 'journal-entries' && <JournalEntries fiscalSponsorId={currentUser?.fiscalSponsorId || ''} />}
        {activeTab === 'chart-of-accounts' && <ChartOfAccounts />}
        {activeTab === 'fund-management' && <FundManagement />}
      </Box>
    </Box>
  );
}; 